// alertFactsHelpers — read the pipeline-owned `alert_facts_json` alert property.
//
// Contract: M365Signal-Databricks-Pipelines/ALERT-FACTS-CONTRACT.md (schema v1, 2026-09-29).
// The pipeline defines the base facts for each alert kind ONCE, at alert creation, and
// every channel (portal, Teams, email, webhook) starts from that set. For a kind that
// has facts, the portal must NOT re-derive the same detail from raw_event_json — that is
// the drift this contract exists to end (our exchangeRuleHelpers.js was one of the three
// copies). When a fact is missing, request it (contract §6), don't parse for it.
//
// Reading rules, all from the contract:
//   * ABSENT means "no facts" (older alert, or a kind with none). Never an error: the
//     caller falls back to today's display. Anything malformed reads as absent too — a
//     broken payload must not blank a panel the fallback could have filled.
//   * Select on `key` / `group`, NEVER on `label` (labels may be reworded unannounced).
//   * IGNORE unknown kinds, flags and groups. An unknown kind returns null so the caller
//     keeps its fallback; an unknown flag is dropped; an unknown group is dropped rather
//     than rendered under a heading we cannot name.
//   * A schema_version above what we understand is a breaking change by definition
//     (contract §5: renaming a key or changing a value's meaning bumps it), so it reads
//     as absent rather than being rendered under v1 assumptions.

export const ALERT_FACTS_SCHEMA_VERSION = 1;

// Kinds this portal build knows how to present. Contract §4. Each kind brings its own
// display groups and flags; a fact in a group the kind does not declare is dropped.
// Display groups for inbox_rule, in contract order (§3: rule, then condition, exception, action).
export const FACT_GROUPS = [
  { group: 'rule', title: 'Rule' },
  { group: 'condition', title: 'Conditions' },
  { group: 'exception', title: 'Exceptions' },
  { group: 'action', title: 'Actions' },
];

// inbox_rule flags (contract §4.1) → chip label + badge tone. Order is display order:
// the ones an analyst should see first come first.
export const INBOX_RULE_FLAGS = [
  { flag: 'forward', label: 'Forwards mail', tone: 'critical' },
  { flag: 'delete', label: 'Deletes mail', tone: 'critical' },
  { flag: 'mark_read', label: 'Marks as read', tone: 'warning' },
  { flag: 'move', label: 'Moves mail', tone: 'warning' },
  { flag: 'stop_processing', label: 'Stops later rules', tone: 'warning' },
  { flag: 'folder_name_unresolved', label: 'Folder name not found', tone: 'neutral' },
];

// local_group_change (contract §4.4, 2026-10-26): who was added to or removed from a local
// group, what that member is, and who made the change. The member comes first because it is
// what the analyst is asking ("who got admin rights").
export const LOCAL_GROUP_CHANGE_GROUPS = [
  { group: 'member', title: 'Member' },
  { group: 'change', title: 'Change' },
  { group: 'actor', title: 'Changed by' },
];

// `added` / `removed` are not chips: change.action states it in words.
export const LOCAL_GROUP_CHANGE_FLAGS = [
  { flag: 'unrecognised_domain', label: 'Member from an unrecognised domain', tone: 'critical' },
  { flag: 'member_is_group', label: 'Member is a group: every member of it is affected', tone: 'warning' },
  { flag: 'group_members_unavailable', label: 'Group members not available', tone: 'neutral' },
  { flag: 'member_unresolved', label: 'Member name not resolved', tone: 'neutral' },
  { flag: 'domain_join', label: 'Matches a domain join', tone: 'info' },
];

// The pipeline's labels for the group's member list ("Group members" / "Members") read as
// though they name who was added or removed. They are the group's members TODAY (the daily
// Entra snapshot), so the portal says so, naming the group. Keyed on `key`, never on the
// pipeline's label. A member name that is a bare SID reads as "this group".
function localGroupChangeLabel(key, byKey) {
  const name = byKey['member.name'];
  const group = isNonEmptyString(name) && !/^S-1-/i.test(name.trim()) ? name.trim() : 'this group';
  if (key === 'member.group_member_count') return `Number in ${group} today`;
  if (key === 'member.group_members') return `Members of ${group} today`;
  return null;
}

// The group chip says which way the change went, from the `added` / `removed` flags.
function localGroupChangeFlagLabel(flag, flagSet) {
  if (flag !== 'member_is_group') return null;
  if (flagSet.has('removed')) return 'Member is a group: everyone in it lost these rights';
  if (flagSet.has('added')) return 'Member is a group: everyone in it gained these rights';
  return null;
}

export const KIND_SPECS = {
  inbox_rule: { groups: FACT_GROUPS, flags: INBOX_RULE_FLAGS },
  local_group_change: {
    groups: LOCAL_GROUP_CHANGE_GROUPS,
    flags: LOCAL_GROUP_CHANGE_FLAGS,
    label: localGroupChangeLabel,
    flagLabel: localGroupChangeFlagLabel,
  },
};
export const KNOWN_KINDS = new Set(Object.keys(KIND_SPECS));

const isNonEmptyString = (v) => typeof v === 'string' && v.trim() !== '';

/** Parse an alert's `alertFactsJson` into a render-ready object, or null.
 *
 *  Accepts the JSON string the row carries, or an already-parsed object.
 *  Returns { kind, headline, flags:[{flag,label,tone}], groups:[{group,title,facts}],
 *            contentless, factCount } — or null whenever the caller should fall back. */
export function parseAlertFacts(input) {
  if (input == null || input === '') return null;
  let doc = input;
  if (typeof input === 'string') {
    // A spilled payload (`blob:` ref) is not facts we can read here.
    if (input.startsWith('blob:')) return null;
    try { doc = JSON.parse(input); } catch { return null; }
  }
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return null;

  const version = Number(doc.schema_version);
  if (!Number.isFinite(version) || version < 1 || version > ALERT_FACTS_SCHEMA_VERSION) return null;
  if (!KNOWN_KINDS.has(doc.kind)) return null;

  const rawFlags = Array.isArray(doc.flags) ? doc.flags : [];
  const flagSet = new Set(rawFlags.filter((f) => typeof f === 'string'));
  const contentless = flagSet.has('contentless');
  // `contentless` is a state, not a chip: it changes what the panel says, below.
  const spec = KIND_SPECS[doc.kind];
  const flags = spec.flags
    .filter((f) => flagSet.has(f.flag))
    .map((f) => ({ ...f, label: spec.flagLabel?.(f.flag, flagSet) || f.label }));
  const knownGroups = new Set(spec.groups.map((g) => g.group));

  const facts = (Array.isArray(doc.facts) ? doc.facts : []).filter((f) =>
    f && typeof f === 'object'
    && isNonEmptyString(f.key)
    && knownGroups.has(f.group)
    && (typeof f.value === 'string' || typeof f.value === 'number' || typeof f.value === 'boolean'));
  const byKey = Object.fromEntries(facts.map((f) => [f.key, String(f.value)]));

  const groups = spec.groups
    .map(({ group, title }) => ({
      group,
      title,
      // Order within a group is the pipeline's (§3 "ordered list") — keep it.
      facts: facts
        .filter((f) => f.group === group)
        .map((f) => ({
          key: f.key,
          // A kind may word a row itself; otherwise the pipeline's label, then the key
          // rather than a blank cell.
          label: spec.label?.(f.key, byKey) || (isNonEmptyString(f.label) ? f.label : f.key),
          value: String(f.value),
        })),
    }))
    .filter((g) => g.facts.length > 0);

  const headline = isNonEmptyString(doc.headline) ? doc.headline : null;

  // Nothing at all to show and not an explicit re-sync notice ⇒ let the fallback render.
  if (!headline && groups.length === 0 && !contentless) return null;

  return {
    kind: doc.kind,
    headline,
    flags,
    groups,
    contentless,
    factCount: facts.length,
    byKey,
    flagSet: [...flagSet],
  };
}

// ── local_group_change: plain-language summary ────────────────────────────────
// The facts table reads as database fields ("Member", two different "Administrators", a
// second event described as a fact of this one). The summary says what happened in
// sentences built ONLY from keys and flags; the pipeline's fixed value prefixes from
// contract §4.4 ("Not available", "Not checked", "None") are the only value text read.
// The facts table is not shown alongside it (the SIDs are in the Raw Event JSON panel).

// Flags the summary does not state in words stay as chips.
export const LOCAL_GROUP_CHANGE_SUMMARY_CHIPS = new Set(['unrecognised_domain', 'member_unresolved']);

/** Build the summary from a parsed local_group_change, or null when a sentence cannot be
 *  built (no member, group or device), so the panel falls back to the facts table. */
export function buildLocalGroupChangeSummary(parsed) {
  if (!parsed || parsed.kind !== 'local_group_change') return null;
  const k = parsed.byKey || {};
  const flags = new Set(parsed.flagSet || []);
  const name = (k['member.name'] || '').trim();
  const group = (k['change.group'] || '').trim();
  const device = (k['change.device'] || '').trim();
  if (!name || !group || !device) return null;

  const action = k['change.action'] || '';
  const removed = flags.has('removed') || /^Removed/i.test(action);
  const added = !removed && (flags.has('added') || /^Added/i.test(action));
  if (!added && !removed) return null;

  const isGroup = flags.has('member_is_group');
  const type = k['member.type'] || '';
  const domain = k['member.domain'] || '';
  let scope = '';
  if (/^Entra/i.test(type)) scope = 'Entra ';
  else if (/^This tenant's on-premises domain/i.test(domain)) scope = 'domain ';
  else if (/^This device's local accounts/i.test(domain) || /\(local\)/i.test(type)) scope = 'local ';
  const noun = isGroup ? 'group' : /^Account or group/i.test(type) ? 'account or group' : 'account';

  const whatHappened = removed
    ? `On computer ${device}, the ${scope}${noun} "${name}" was REMOVED from the computer's local ${group} group.`
    : `On computer ${device}, the ${scope}${noun} "${name}" was ADDED to the computer's local ${group} group.`;

  const isAdmins = k['change.group_sid'] === 'S-1-5-32-544' || /^Administrators$/i.test(group);
  const who = isGroup ? `Members of ${name}` : name;
  const get = isGroup ? 'get' : 'gets';
  const via = isGroup ? 'this group' : 'this membership';
  let effect;
  if (isAdmins) {
    effect = removed
      ? `${who} no longer ${get} admin rights on ${device} through ${via}.`
      : `${who} can now administer ${device}.`;
  } else {
    effect = removed
      ? `${who} no longer ${get} the rights of the local ${group} group on ${device} through ${via}.`
      : `${who} now ${get} the rights of the local ${group} group on ${device}.`;
  }

  // Group members: listed, empty, unavailable or not checked.
  let members = null;
  if (isGroup) {
    const raw = k['member.group_members'] || '';
    const count = k['member.group_member_count'] || '';
    const source = k['member.group_members_source'] || '';
    if (/^Not available/i.test(raw) || (!raw && flags.has('group_members_unavailable'))) {
      members = { note: `Who is in ${name}: not available to the portal. This group's membership is not synced to Entra, so check it in Active Directory.` };
    } else if (/^Not checked/i.test(raw)) {
      members = { note: `Who is in ${name}: could not be looked up for this alert.` };
    } else if (/^None/i.test(raw)) {
      members = { note: `${name} has no members today.` };
    } else if (raw) {
      members = {
        heading: `Who is in ${name} today${count ? ` (${count})` : ''}`,
        list: raw,
        source,
      };
    }
  }

  // Domain join: the companion Domain Users → Users event, stated as a second event.
  let domainJoin = null;
  if (flags.has('domain_join')) {
    const secs = /(\d+)\s*s apart/i.exec(k['change.domain_join'] || '');
    const when = secs && Number(secs[1]) <= 5 ? 'At the same moment' : secs ? `${secs[1]} seconds apart` : 'Within two minutes';
    domainJoin = `${when}, a second change on ${device} added "Domain Users" to the computer's local Users group. `
      + `Windows makes both changes when a computer joins the domain, so this is most likely ${device} being joined to the domain.`;
  }

  return {
    whatHappened,
    doneBy: k['actor.name'] || null,
    effect,
    members,
    domainJoin,
    chips: parsed.flags.filter((f) => LOCAL_GROUP_CHANGE_SUMMARY_CHIPS.has(f.flag)),
  };
}

// ── User column: who did it and to whom ───────────────────────────────────────
// A local_group_change alert's user_id is the MEMBER's SID, which on a local account
// Microsoft did not name is all the row can show ("S-1-5-21-…"). The facts carry both
// sides, so the User column reads "<actor> added <member>". Built from keys only.

// A SID is long and only its last number tells two accounts on one machine apart.
export function shortSid(value) {
  const s = String(value || '').trim();
  const m = /^(S-1-5-21)-\d+-\d+-\d+-(\d+)$/i.exec(s);
  return m ? `${m[1]}-…-${m[2]}` : s;
}

/** For a local_group_change alert, the User column's text, copy value and hover text.
 *  Accepts the row's alertFactsJson (string or object). Returns null for any other alert,
 *  or when the facts name no member, so the caller keeps its own display. */
export function alertFactsUserCell(alertFactsJson) {
  const parsed = parseAlertFacts(alertFactsJson);
  if (!parsed || parsed.kind !== 'local_group_change') return null;
  const k = parsed.byKey || {};
  const flags = new Set(parsed.flagSet || []);
  const member = (k['member.name'] || k['member.sid'] || '').trim();
  if (!member) return null;
  // actor.name is "<name> (local account on <device>)" / "(Entra ID account)" / DOMAIN\name / UPN.
  const actor = (k['actor.name'] || '').replace(/\s*\((local account on|Entra ID account)[^)]*\)\s*$/i, '').trim();
  const removed = flags.has('removed') || /^Removed/i.test(k['change.action'] || '');
  const verb = removed ? 'removed' : 'added';
  const memberShort = shortSid(member);
  return {
    display: actor ? `${actor} ${verb} ${memberShort}` : `${memberShort} (${verb})`,
    copy: member,
    title: parsed.headline || member,
  };
}
